/* eslint-disable no-magic-numbers */
import { getRel, getRelX, getRelY } from './utils/getRel';
import { scaleToCover } from './utils/scaleTo';
import { fontFamily } from './constants';
import { AdvancedBloomFilter } from '@pixi/filter-advanced-bloom';
import detectTvScreen from './utils/detectTvScreen';

export function ItemAvatar(texture, gradientTexture, color, alt) {
	alt = !!alt;  // alternative position
	const avatarCont = new PIXI.Container();
	const avatar = new PIXI.Sprite(texture);
	const avatarGradient = new PIXI.Sprite(gradientTexture);
	const avatarSize = getRelX(0.5);
	const avatarMask = new PIXI.Graphics();
	avatarMask.beginFill(0x00FF00, 1);
	avatarMask.drawCircle(0, 0, avatarSize / 2);
	avatarCont.anchor = new PIXI.Point(0.5, 0.5);
	avatarGradient.anchor = new PIXI.Point(0.5, 1);
	avatarMask.anchor = new PIXI.Point(0.5, 0.5);
	avatar.anchor = new PIXI.Point(0.5, 0.5);
	scaleToCover(avatar, avatarSize, avatarSize);
	avatarGradient.width = avatar.width;
	avatarGradient.height = avatar.height / 2;
	avatarGradient.rotation = Math.PI;
	avatarGradient.tint = color.clone().darken(10).toNumber();
	avatarGradient.alpha = 0.8;
	if (alt) {
		avatarCont.position = new PIXI.Point(getRelX(1) + avatarSize, getRelY(1));
	} else {
		avatarCont.position = new PIXI.Point(getRelX(0) - avatarSize, getRelY(1));
	}
	avatarCont.addChildAt(avatar, 0);
	avatarCont.addChildAt(avatarMask, 1);
	avatarCont.addChildAt(avatarGradient, 2);
	avatarCont.mask = avatarMask;
	avatarCont.cacheAsBitmap = true;

	return avatarCont;
}

export function ItemBar(color, alt) {
	// colored bar that slides in
	alt = !!alt;
	const bar = new PIXI.Graphics();
	bar.beginFill(color.clone().darken(10).toNumber());
	const w = getRelX(1.4);
	const h = getRelY(0.28);
	bar.drawRect(-w / 2, -h / 2, w, h);
	bar.anchor = new PIXI.Point(0.5, 0.5);
	bar.position = getRel(0.5, 0.9);
	if (alt) {
		bar.rotation = Math.PI * -0.035;
		bar.position.x = 0 - w / 1.9;
	} else {
		bar.rotation = Math.PI * 0.035;
		bar.position.x = getRelX(1) + w / 1.9;
	}
	bar.cacheAsBitmap = true;

	return bar;
}

// eslint-disable-next-line max-params
export function ItemBarText(relFontSize, color, nameText, targetText, targetTextLabel, alt) {

	// white name and text on top of the bar
	alt = !!alt;
	const text = new PIXI.Container();
	text.anchor = new PIXI.Point(0.5, 0.5);

	// move it out of the stage
	text.position = getRel(1.5, 0.70);
	if (alt) {
		text.position.x = -getRelX(0.5);
	}

	if (nameText.split(' ').length > 1) {

		// use a big wrapWidth, but also break line manually at least once
		nameText = nameText.replace(' ', '\n');
	}
	const name = new PIXI.Text(nameText.toUpperCase(), {
		fontWeight: 'bold',
		fontSize: (relFontSize * (nameText.length > 22 ? 0.85 : 1)) + 'px',
		fontFamily: fontFamily,
		fill: '#FFFFFF',
		align: 'center',
		dropShadow: true,
		dropShadowDistance: 2,
		dropShadowColor: color.clone().darken(30).toHexString(),
		wordWrap: true,
		wordWrapWidth: getRelX(0.5),
	});
	name.anchor = new PIXI.Point(0.5, 0);
	name.position = getRel(0.22, 0.075);
	name.rotation = Math.PI * 0.035;
	name.scale.x = name.scale.y = 1.5;
	if (alt) {
		name.rotation = Math.PI * -0.035;
		name.position.x = -getRelX(0.22);
	}
	text.addChild(name);
	if (targetText && targetText !== '0') {
		const target = new PIXI.Text((targetTextLabel).toUpperCase(), {
			fontWeight: 'normal',
			fontSize: Math.floor(relFontSize * 0.35) + 'px',
			fontFamily: fontFamily,
			fill: '#FFFFFF',
			align: 'center',
			dropShadow: true,
			dropShadowDistance: 2,
			dropShadowColor: color.clone().darken(30).toHexString(),
		});
		const targetVal = new PIXI.Text(targetText, {
			fontWeight: 'normal',
			fontSize: Math.floor(relFontSize * 0.65) + 'px',
			fontFamily: fontFamily,
			fill: '#FFFFFF',
			align: 'center',
			dropShadow: true,
			dropShadowDistance: 2,
			dropShadowColor: color.clone().darken(30).toHexString(),
			wordWrap: true,
			wordWrapWidth: getRelX(0.3),
		});
		targetVal.anchor = new PIXI.Point(0.5, 1);
		target.anchor = new PIXI.Point(0.5, 1);
		targetVal.position = getRel(0.228, 0);
		target.position = getRel(0.23, -0.10);
		targetVal.rotation = Math.PI * 0.035;
		target.rotation = Math.PI * 0.035;
		target.scale.x = target.scale.y = 1.5;
		targetVal.scale.x = targetVal.scale.y = 1.5;
		if (alt) {
			targetVal.position.x = getRelX(-0.226);
			target.position.x = getRelX(-0.232);
			targetVal.rotation = Math.PI * -0.035;
			target.rotation = Math.PI * -0.035;
		}
		text.addChild(targetVal);
		text.addChild(target);
	}
	return text;
}

export function ItemBg(texture, color, width, height) {

	// colorized and blurred avatar as the cover background
	const bg = new PIXI.Sprite(texture);
	scaleToCover(bg, width, height);
	bg.anchor = new PIXI.Point(0.5, 0.5);
	bg.position = getRel(0.5, 0.5);
	bg.tint = color.clone().darken(30).toNumber();

	const blur = new PIXI.filters.BlurFilter();
	blur.blur = 30;
	blur.quality = 8;

	const bloom = new AdvancedBloomFilter();
	bloom.bloomScale = 1.5;

	const isTV = detectTvScreen();
	// For the sake of performance
	// we should not apply blur effect on TV browsers
	bg.filters = [bloom, (!isTV && blur)];

	return bg;
}

